import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from 'src/app/services/config/config.service';
import { firstValueFrom } from 'rxjs';
import { Constants } from '../constants';
import { Claim } from 'src/app/services/accounts/accounts.model';
import { ResponseInterface } from '../interfaces/response.interface';
import { removeEmptyClaims } from '../utils/remove-empty-claims';

@Injectable({
  providedIn: 'root',
})
export class RegistrationService {
  /**
   * Holds the actual data of the personal info form submitted to the registration API
   */
  public readonly personalInfo = signal<Record<string, string>>(undefined);

  public readonly personalInfoCache = signal<Record<string, string>>(undefined);

  public readonly personalInfoConsents = signal<number[]>(undefined);

  public readonly registeredUserData = signal<{ synlabId: string; userId: string }>(undefined);

  public readonly fromActivation = signal<boolean>(undefined);

  /**
   * Holds the actual data of the complete details form submitted to the registration API
   */
  public readonly completeDetails = signal<Record<string, string>>(undefined);

  public readonly completeDetailsCache = signal<Record<string, string>>(undefined);

  constructor(
    private readonly http: HttpClient,
    private readonly configService: ConfigService,
  ) {}

  public saveOrgId(orgId: string) {
    const { BaseAuthUrl } = this.configService.envConfig;
    const url = Constants.saveOrgUrl.replace('[selectedOrgHere]', orgId);
    const request = this.http.post<ResponseInterface<{ synlabId: string; userId: string }>>(
      `${BaseAuthUrl}${url}`,
      {},
    );
    return firstValueFrom(request);
  }

  public registerCustomer(
    email: string,
    password: string,
    consents: number[],
    isFromBooking: boolean = false,
  ) {
    const { BaseAuthUrl } = this.configService.envConfig;
    const request = this.http.post<ResponseInterface<{ synlabId: string; userId: string }>>(
      `${BaseAuthUrl}${Constants.signUpUrl}`,
      {
        EmailAddress: email,
        Consents: consents.join(','),
        Password: password,
        IsFromBooking: isFromBooking,
      },
    );
    return firstValueFrom(request);
  }

  public getProfileAnonymously(userId: string, email: string) {
    const { BaseAuthUrl } = this.configService.envConfig;
    const request = this.http.get<Claim[]>(
      `${BaseAuthUrl}${Constants.getProfileAnonymously}?email=${email}`, // prevent email query param from url encoding
      {
        params: {
          id: userId,
        },
      },
    );
    return firstValueFrom(request);
  }

  public registerGlobalCustomer(
    email: string,
    password: string,
    orgId: string,
    consents: number[],
    italian: boolean,
    dateOfBirth: string,
    fiscalCode: string = null,
    isFromBooking: boolean = false,
  ) {
    const { BaseAuthUrl } = this.configService.envConfig;
    const dob = !dateOfBirth ? '' : this.formatDateOfBirth(dateOfBirth);
    const request = this.http.post<ResponseInterface<{ synlabId: string; userId: string }>>(
      `${BaseAuthUrl}${Constants.globalSignUpUrl}`,
      {
        EmailAddress: email,
        Consents: consents.join(','),
        Password: password,
        FiscalCode: fiscalCode,
        Italian: italian,
        IsFromBooking: isFromBooking,
        DateOfBirth: italian ? '' : dob,
        OrgId: orgId || null,
      },
    );
    return firstValueFrom(request);
  }

  public registerITCustomer(
    email: string,
    password: string,
    consents: number[],
    italian: boolean,
    dateOfBirth: string,
    fiscalCode: string = null,
    isFromBooking: boolean = false,
    userId: string = null,
  ) {
    const { BaseAuthUrl } = this.configService.envConfig;
    const dob = !dateOfBirth ? '' : this.formatDateOfBirth(dateOfBirth);
    const isItalian = fiscalCode ? italian : false;

    const request = this.http.post<ResponseInterface<{ synlabId: string; userId: string }>>(
      `${BaseAuthUrl}${Constants.itSignUpUrl}`,
      {
        EmailAddress: email,
        Consents: consents.join(','),
        Password: password,
        FiscalCode: fiscalCode,
        Italian: isItalian,
        IsFromBooking: isFromBooking,
        DateOfBirth: isItalian ? '' : dob,
        UserId: userId,
      },
    );
    return firstValueFrom(request);
  }

  // eslint-disable-next-line class-methods-use-this
  private formatDateOfBirth(dateOfBirth: string) {
    const parts = dateOfBirth.split('.');
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }

  public get isItalian() {
    const personalInfo = this.personalInfo();
    const isItalian = personalInfo
      ? !personalInfo?.noFiscalCode
      : !this.personalInfoCache()?.noFiscalCode;
    return Boolean(isItalian);
  }

  public async itCompleteProfile(id: string, claims: Claim[]) {
    const { BaseAuthUrl } = this.configService.envConfig;
    const request = this.http.put(
      `${BaseAuthUrl}${Constants.itCompleteProfileUrl}`,
      removeEmptyClaims(claims),
      {
        params: { id },
      },
    );
    return firstValueFrom(request);
  }

  public async completeProfile(id: string, claims: Claim[]) {
    const { BaseAuthUrl } = this.configService.envConfig;
    const request = this.http.put(
      `${BaseAuthUrl}${Constants.completeProfileUrl}`,
      removeEmptyClaims(claims),
      {
        params: { id },
      },
    );
    return firstValueFrom(request);
  }

  public async checkIfMigrated(email: string) {
    const { BaseAuthUrl } = this.configService.envConfig;
    const request = this.http.get<{ isMigrated: boolean }>(
      `${BaseAuthUrl}${Constants.getMigratedRelatedInfo}`,
      {
        params: {
          userValue: email,
        },
      },
    );
    return firstValueFrom(request);
  }
}
